import DesktopAwareNotification from "../core-components/DesktopAwareNotification";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { doc, getDoc, onSnapshot, updateDoc } from "firebase/firestore";

import { db } from "../../Security/firebase-config";
import { useAuth } from "../../context/AuthContext";
import { useTeam } from "../../context/TeamContext";
import { getRequestedTeamId } from "../../utils/teamRouteTarget";
import "../core-components/Team-Overview.css";
import "./TeamCenterFrame.css";
import TeamCenterNavigation from "./TeamCenterNavigation";
import TeamNamePicker from "./TeamNamePicker";

function formatCreatedDate(value) {
  const date = value?.toDate?.() || (value instanceof Date ? value : value ? new Date(value) : null);
  if (!date || Number.isNaN(date.getTime())) return "Not available";

  return new Intl.DateTimeFormat("en-CA", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function getTeamSizeLabel(team) {
  const members = Array.isArray(team?.members) ? team.members.length : null;
  const maxMembers = Number(team?.maxMembers || team?.memberLimit || team?.teamSizeLimit || team?.teamSize);
  if (members !== null && Number.isFinite(maxMembers) && maxMembers > 0) return `${members} / ${maxMembers} members`;
  if (members !== null) return `${members} member${members === 1 ? "" : "s"}`;
  return "Team roster";
}

export default function TeamCenterFrame({
  className = "",
  title,
  description,
  actions = null,
  children,
  identityLabel = "Team Center",
  showPageTitle = true,
  showNavigation = true,
  showTeamPicker = true,
  showTeamFacts = false,
  loadTeamIdentity = true,
  mark = null,
  identityTitle = null,
  knownTeam = null,
  footer = null,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { userData } = useAuth();
  const { teamId, setTeamId, teamName } = useTeam();
  const routeTeamId = getRequestedTeamId(location.search) || teamId;
  const [teams, setTeams] = useState([]);
  const [routeTeam, setRouteTeam] = useState(null);
  const [teamSwitching, setTeamSwitching] = useState(false);
  const [teamSwitchError, setTeamSwitchError] = useState(null);
  const teamIdsKey = JSON.stringify(Array.isArray(userData?.teamIds) ? userData.teamIds : []);

  useEffect(() => {
    let cancelled = false;
    const teamIds = JSON.parse(teamIdsKey);

    if (!loadTeamIdentity || !teamIds.length) {
      setTeams([]);
      return () => { cancelled = true; };
    }

    Promise.all(teamIds.map(async (id) => {
      const snapshot = await getDoc(doc(db, "teams", id));
      if (!snapshot.exists()) return null;
      const data = snapshot.data() || {};
      return { id: snapshot.id, name: data.teamName || data.name || "Unnamed Team", data };
    }))
      .then((results) => {
        if (!cancelled) setTeams(results.filter(Boolean));
      })
      .catch((error) => {
        console.error("Failed to load teams for the Team Center picker:", error);
        if (!cancelled) setTeams([]);
      });

    return () => { cancelled = true; };
  }, [teamIdsKey, loadTeamIdentity]);

  useEffect(() => {
    let cancelled = false;
    if (!loadTeamIdentity) {
      setRouteTeam(null);
      return () => { cancelled = true; };
    }
    if (!routeTeamId) {
      setRouteTeam(null);
      return () => { cancelled = true; };
    }

    setRouteTeam(null);
    const stop = onSnapshot(doc(db, "teams", routeTeamId), snapshot => {
      if (!cancelled) setRouteTeam(snapshot.exists() ? snapshot.data() || {} : null);
    }, error => {
      console.error("Failed to refresh the current Team Center team:", error);
      if (!cancelled) setRouteTeam(null);
    });
    return () => {cancelled = true; stop();};
  }, [routeTeamId, loadTeamIdentity]);

  const selectedTeam = teams.find((team) => team.id === routeTeamId);
  const displayTeamName = routeTeam?.teamName || routeTeam?.name || (knownTeam?.id === routeTeamId && knownTeam.name) || selectedTeam?.name || teamName || "Unnamed Team";
  const pickerTeams = useMemo(() => {
    if (!routeTeamId) return teams;
    if (teams.some((team) => team.id === routeTeamId)) return teams.map(team => team.id === routeTeamId ? {...team, name: displayTeamName} : team);
    return [{ id: routeTeamId, name: displayTeamName }, ...teams];
  }, [displayTeamName, routeTeamId, teams]);
  const displayTeamId = selectedTeam?.id || routeTeamId || teamId || "";

  const handleTeamChange = async (nextTeamId) => {
    if (!nextTeamId || nextTeamId === displayTeamId || !userData?.uid || teamSwitching) return;
    setTeamSwitching(true);
    setTeamSwitchError(null);
    try {
      await updateDoc(doc(db, "users", userData.uid), { activeTeamId: nextTeamId });
      setTeamId(nextTeamId);
      const params = new URLSearchParams(location.search);
      params.set("teamId", nextTeamId);
      navigate({ pathname: location.pathname, search: `?${params.toString()}` });
    } catch (error) {
      console.error("Failed to store the active Team Center team:", error);
      setTeamSwitchError("The team switch could not be saved. Please try again.");
    } finally {
      setTeamSwitching(false);
    }
  };

  const teamFacts = showTeamFacts ? [
    ["Owner", routeTeam?.teamLeaderUsername || routeTeam?.ownerUsername || "Not available"],
    ["Created", formatCreatedDate(routeTeam?.createdAt)],
    ["Status", routeTeam?.activityStatus || "Active"],
    ["Region", routeTeam?.teamRegion || "Not available"],
    ["Time zone", routeTeam?.teamTimeZone || "UTC"],
    ["Team size", getTeamSizeLabel(routeTeam)],
  ] : [];
  const resolvedIdentityTitle = identityTitle || (
    showTeamPicker
      ? null
      : title
  );

  return (
    <main className={`TmO-dashboard TeamCenterFrame ${className}`.trim()} aria-label={`${title} Team Center page`}>
      <div className="TeamCenterFrame__shell">
        {showNavigation ? <TeamCenterNavigation teamId={routeTeamId} /> : null}

        <header className="TmO-dashboard__identityHero TeamCenterFrame__identityCard">
          <div className="TmO-dashboard__teamMark" aria-hidden="true">
            {String(mark || displayTeamName || "T").trim().slice(0, 1).toUpperCase()}
          </div>
          <div className="TmO-dashboard__identityCopy TeamCenterFrame__identityCopy">
            <span>{identityLabel}</span>
            <h1>
              {showTeamPicker ? (
                <TeamNamePicker
                  className="TeamCenterFrame__teamPicker"
                  teams={pickerTeams}
                  value={displayTeamId}
                  fallbackLabel={displayTeamName}
                  onChange={handleTeamChange}
                  disabled={teamSwitching}
                />
              ) : resolvedIdentityTitle ? resolvedIdentityTitle : <span className="TeamCenterFrame__teamName">{displayTeamName}</span>}
              {showPageTitle ? <><span className="TeamCenterFrame__titleSpacer"> </span>{title}</> : null}
            </h1>
            {description ? <p>{description}</p> : null}
            {teamFacts.length ? (
              <div className="TmO-dashboard__identityFacts TeamCenterFrame__identityFacts">
                {teamFacts.map(([label, value]) => <span key={label}><strong>{label}</strong>{value}</span>)}
              </div>
            ) : null}
          </div>
          {actions ? <div className="TeamCenterFrame__actions">{actions}</div> : null}
        </header>

        {teamSwitchError ? <DesktopAwareNotification as="p" role="alert">{teamSwitchError}</DesktopAwareNotification> : null}
        <div className="TeamCenterFrame__content">{children}</div>
      </div>
      {footer ? <div className="TeamCenterFrame__footer">{footer}</div> : null}
    </main>
  );
}
